// Task #3720 — DTO risposta condiviso di `POST /pos/sales`.
//
// Contratto dati BE↔FE: schema Zod di RISPOSTA + tipo `z.infer`, usato come
// tipo di ritorno del service backend (pos-sale.service.createSale) e come
// generic di `apiClient.post` nel POS frontend (modale "Vendita Completata").
// Fedeltà al WIRE reale (JSON servito dal backend):
//  - la testata vendita è `INSERT ... RETURNING *` su pos_sale_headers → le
//    colonne numeric(x,y) viaggiano come STRINGA (driver pg), ma il service
//    sovrascrive alcuni campi con number calcolati → z.union([string, number]);
//  - timestamp/timestamptz → z.union([z.string(), z.date()]) (wire = ISO
//    string; Date lato BE pre-serializzazione) — convenzione crm.ts;
//  - colonne NULLABLE nello schema DB → .nullable();
//  - `RETURNING *` → colonne enumerate dallo schema DB
//    (packages/database/db/schema.sql) chiuse con `.passthrough()`.
//
// Envelope wire (gestito da apiClient): createdResponse → { success:true, data:<value>, message }.

import { z } from 'zod';
import type { PosSaleFiscalOutcome } from '../types/api';
import { ADMIN_EXPECTATION_KINDS, ADMIN_EXPECTATION_STATUSES, SaleDetailCanvasSnapshotSchema } from './pricelist';
import { INSTALLMENT_REPAYMENTS, PAYMENT_MODE_ACCOUNTING_EXPECTATIONS, PAYMENT_MODE_ADMIN_EXPECTATIONS, PAYMENT_MODE_CREDIT_HOLDERS } from './payment-modes';
import type { VatCalculationMode } from '../fiscal/vat-calculator';
import type { CommissioningSaleAddonAnswerInput } from '../schemas/commissioning';

/** timestamp/timestamptz: ISO string sul wire, Date lato BE pre-serializzazione. */
const wireTimestamp = z.union([z.string(), z.date()]);
/** pg numeric(x,y): stringa sul wire (driver pg), number quando ricalcolato dal service. */
const wireNumeric = z.union([z.string(), z.number()]);

/**
 * Componente di combinazione listino risolto server-side allegato alla riga
 * processata (Task #3275/#4320): economia autoritativa + modalità di pagamento.
 * Importi: number quando risolti dal service, string quando letti raw dal
 * driver pg (numeric) → wireNumeric.
 */
export const PosSaleCompositionResolvedSchema = z.object({
  id: z.string(),
  price: wireNumeric,
  discount_percent: wireNumeric.nullable(),
  discount_amount: wireNumeric.nullable(),
  device_advance_amount: wireNumeric.nullable(),
  installments_count: wireNumeric.nullable(),
  installment_amount: wireNumeric.nullable(),
  financed_amount: wireNumeric.nullable(),
  price_list_id: z.string(),
  /** Snapshot promo: id/nome composizione, NDC fornitore attesa unitaria, fornitore del listino. */
  composition_id: z.string(),
  composition_name: z.string().nullable(),
  supplier_credit_note_unit: wireNumeric.nullable(),
  price_list_supplier_id: z.string().nullable(),
  // Modalità di pagamento all'insegna della composizione (autoritativa, solo
  // bundle: null = device_promo non finanziata) + ente finanziante del tenant
  // (solo ente terzo) + addebito effettivo della composizione.
  payment_mode_id: z.string().nullable(),
  payment_mode_code: z.string().nullable(),
  payment_mode_name: z.string().nullable(),
  credit_holder: z.enum(PAYMENT_MODE_CREDIT_HOLDERS).nullable(),
  accounting_expectation: z.enum(PAYMENT_MODE_ACCOUNTING_EXPECTATIONS).nullable(),
  admin_expectation: z.enum(PAYMENT_MODE_ADMIN_EXPECTATIONS).nullable(),
  installment_repayment: z.enum(INSTALLMENT_REPAYMENTS).nullable(),
  financing_partner_id: z.string().nullable(),
}).passthrough();

export type PosSaleCompositionResolved = z.infer<typeof PosSaleCompositionResolvedSchema>;

/**
 * Task #4322 — struttura di credito cristallizzata sulla riga di vendita
 * (snapshot delle colonne `pos_sale_details.*`): modalità (riferimento +
 * codice/nome/titolare/rimborso), ente, finanziato unitario, rate, rata,
 * anticipo, attese e pratica collegata. Derivata SOLO server-side dalla
 * composizione bundle risolta (mai dal client); assente = riga non finanziata.
 */
export const PosSaleCreditSnapshotSchema = z.object({
  payment_mode_id: z.string().uuid(),
  payment_mode_code: z.string(),
  payment_mode_name: z.string(),
  credit_holder: z.enum(PAYMENT_MODE_CREDIT_HOLDERS),
  /** Addebito effettivo della composizione venduta. */
  installment_repayment: z.enum(INSTALLMENT_REPAYMENTS),
  accounting_expectation: z.enum(PAYMENT_MODE_ACCOUNTING_EXPECTATIONS),
  admin_expectation: z.enum(PAYMENT_MODE_ADMIN_EXPECTATIONS),
  financing_partner_id: z.string().uuid().nullable(),
  financed_amount: wireNumeric.nullable(),
  installments_count: z.number().int().nullable(),
  installment_amount: wireNumeric.nullable(),
  device_advance_amount: wireNumeric.nullable(),
  financing_contract_id: z.string().uuid().nullable(),
  /** Task #4334 — metodo/RT/MP effettivi usati per la quota finanziata (null = riga senza quota). */
  financed_quota_payment_method_id: z.string().uuid().nullable(),
  financed_quota_method_name: z.string().nullable(),
  financed_quota_ade_code: z.number().int().nullable(),
  financed_quota_mp_code: z.string().nullable(),
});
export type PosSaleCreditSnapshot = z.infer<typeof PosSaleCreditSnapshotSchema>;

/**
 * Task #4322 — vista credito della riga nel dettaglio vendita (storico):
 * snapshot `pos_sale_details` + ente/pratica/attese collegati (join server-side).
 */
export const PosSaleDetailAdminExpectationViewSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(ADMIN_EXPECTATION_KINDS),
  status: z.enum(ADMIN_EXPECTATION_STATUSES),
  expected_amount: wireNumeric,
  /** Task #4331 — valorizzato solo per status 'cancelled'. */
  cancel_reason: z.string().nullable(),
});
export type PosSaleDetailAdminExpectationView = z.infer<typeof PosSaleDetailAdminExpectationViewSchema>;

export const PosSaleDetailCreditViewSchema = PosSaleCreditSnapshotSchema.extend({
  financing_partner_name: z.string().nullable(),
  financing_contract_cir: z.string().nullable(),
  financing_contract_status: z.string().nullable(),
  /** Task #4331 — motivo annullo pratica (null se non annullata). */
  financing_contract_cancel_reason: z.string().nullable(),
  /** Composizione di origine (snapshot sulla riga). */
  composition_name: z.string().nullable(),
  admin_expectations: z.array(PosSaleDetailAdminExpectationViewSchema),
});
export type PosSaleDetailCreditView = z.infer<typeof PosSaleDetailCreditViewSchema>;

/**
 * Vista promozione prodotto della riga nel dettaglio vendita: composizione
 * risolta SENZA modalità → promo di origine (snapshot nome composizione e
 * listino), NDC fornitore attesa unitaria, controparte fornitore e attese
 * collegate. Assente (null) sulle righe finanziate o senza composizione.
 */
export const PosSaleDetailPromoViewSchema = z.object({
  /** Marker stabile della promo (snapshot senza FK): presente anche dopo replace/cancellazione della composizione. */
  composition_id: z.string().uuid(),
  composition_product_id: z.string().uuid().nullable(),
  composition_name: z.string().nullable(),
  price_list_id: z.string().uuid().nullable(),
  price_list_name: z.string().nullable(),
  supplier_credit_note_unit: wireNumeric.nullable(),
  supplier_id: z.string().uuid().nullable(),
  supplier_name: z.string().nullable(),
  admin_expectations: z.array(PosSaleDetailAdminExpectationViewSchema),
});
export type PosSaleDetailPromoView = z.infer<typeof PosSaleDetailPromoViewSchema>;

/**
 * Riga vendita processata ritornata in `items` da `posSaleService.createSale`
 * (shape `ProcessedItemPersist` del backend: input riga + campi calcolati dal
 * service + metadata IVA mode-aware + riga sintetica entry fee canvas).
 * Gli importi sono calcolati dal service (number), ma restano wireNumeric
 * (number|string) per coerenza col contratto wire della testata.
 * NB: le battute manuali (manual_items) NON compaiono in `items` — sono
 * persistite direttamente in pos_sale_details.
 */
export const PosSaleProcessedItemSchema = z.object({
  // ── Input riga (SaleItemInput) ──
  product_id: z.string(),
  variant_id: z.string().optional(),
  lot_id: z.string().optional(),
  quantity: z.number(),
  unit_price: wireNumeric,
  discount_percent: wireNumeric.optional(),
  vat_code: z.string(),
  vat_rate: wireNumeric,
  price_list_id: z.string().optional(),
  price_list_name: z.string().optional(),
  is_fiscal: z.boolean().optional(),
  is_gift: z.boolean().optional(),
  serial_number: z.string().optional(),
  composition_product_id: z.string().optional(),
  addon_responses: z.array(z.custom<CommissioningSaleAddonAnswerInput>()).optional(),
  payment_mode_id: z.string().uuid().nullable().optional(),
  /** Task #4322 — snapshot credito ereditato (server-side). */
  credit_snapshot: PosSaleCreditSnapshotSchema.nullable().optional(),
  // ── Campi calcolati dal service ──
  line_net: wireNumeric,
  line_tax: wireNumeric,
  line_total: wireNumeric,
  discount_amount: wireNumeric,
  unit_price_gross: wireNumeric,
  unit_price_net: wireNumeric,
  vat_amount_unit: wireNumeric,
  is_manual: z.boolean().optional(),
  name: z.string().optional(),
  // ── Metadata IVA mode-aware + persist (ProcessedItemPersist) ──
  vat_rate_id: z.string().nullable(),
  vat_calculation_mode: z.custom<VatCalculationMode>(),
  purchase_price_unit: wireNumeric.nullable(),
  margin_method: z.string().nullable(),
  composition_resolved: PosSaleCompositionResolvedSchema.nullable(),
  entry_fee_synthetic: z.boolean().optional(),
  entry_fee_label: z.string().optional(),
  entry_fee_inherit_method_id: z.string().nullable().optional(),
  canvas_snapshot: SaleDetailCanvasSnapshotSchema.omit({
    id: true, tenant_id: true, sale_id: true, sale_detail_id: true, created_at: true,
  }).optional(),
}).passthrough();

export type PosSaleProcessedItem = z.infer<typeof PosSaleProcessedItemSchema>;

/**
 * Record vendita ritornato da `posSaleService.createSale` (testata
 * pos_sale_headers RETURNING * + campi arricchiti dal service: righe
 * processate, totali fiscale/non-fiscale, estremi Attestato di Vendita).
 */
export const PosSaleCreatedRecordSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  sale_number: z.string(),
  sale_date: wireTimestamp,
  customer_id: z.string().nullable(),
  customer_name: z.string().nullable(),
  customer_email: z.string().nullable(),
  warehouse_id: z.string().nullable(),
  payment_method_id: z.string().nullable(),
  sale_type: z.string(),
  status: z.string(),
  subtotal: wireNumeric,
  discount_amount: wireNumeric,
  discount_percent: wireNumeric.nullable(),
  tax_amount: wireNumeric,
  total: wireNumeric,
  notes: z.string().nullable(),
  fiscal_receipt_id: z.string().nullable(),
  fiscal_receipt_data: z.unknown(),
  created_by: z.string().nullable(),
  created_at: wireTimestamp,
  updated_at: wireTimestamp,
  person_id: z.string().nullable(),
  lead_id: z.string().nullable(),
  campaign_id: z.string().nullable(),
  /** Somma delle sole righe fiscali (number calcolato dal service). */
  fiscal_total: wireNumeric.nullable(),
  /** Somma delle righe NON fiscali (Attestato di Vendita). */
  non_fiscal_total: wireNumeric,
  pos_terminal_transaction_id: z.string().nullable(),
  store_id: z.string(),
  company_id: z.string(),
  delivered_at: wireTimestamp.nullable(),
  /** Righe vendita processate (schema condiviso, no drift sui dettagli scontrino). */
  items: z.array(PosSaleProcessedItemSchema),
  /** Estremi Attestato di Vendita auto-generato (solo se non_fiscal_total > 0). */
  attestato_number: z.string().optional(),
  attestato_id: z.string().optional(),
}).passthrough();

export type PosSaleCreatedRecord = z.infer<typeof PosSaleCreatedRecordSchema>;

/** Cassetto contanti aperto/incassato in orchestrazione pagamenti (se presente). */
export const PosSaleCashDrawerOutcomeSchema = z.object({
  drawerId: z.string(),
  collectedCents: z.number(),
  changeCents: z.number(),
});
export type PosSaleCashDrawerOutcome = z.infer<typeof PosSaleCashDrawerOutcomeSchema>;

/**
 * Payload `data` della risposta di `POST /pos/sales`: record vendita + esito
 * emissione scontrino server-side (`fiscal`, tipo condiviso PosSaleFiscalOutcome)
 * + esito cassetto contanti.
 */
export const PosSaleCreateResponseSchema = PosSaleCreatedRecordSchema.extend({
  fiscal: z.custom<PosSaleFiscalOutcome>().optional(),
  cash_drawer: PosSaleCashDrawerOutcomeSchema.optional(),
});

export type PosSaleCreateResponse = z.infer<typeof PosSaleCreateResponseSchema>;
